import { Injectable } from '@angular/core';

import { FailureSummary, SuccessSummary, UpdateResultSummary, VehicleUpdateSummary } from '../../interfaces/update-vehicles-action.interface';
import { UpdateErrorDisplay } from '../../components/action-list-modals/update-selected-modal/update-selected-modal.component';
import { AddVehicleToSelections, AuctionItemSell, ClearAllSelectedVehicles, MultiResultModalItemResult } from 'remarketing-angular-library';
import { Store } from '@ngxs/store';

import {
  MultiResultContentBase,
  MultiResultContentErrorPath,
  MultiResultContentHappyPath,
  MultiResultModalTemplate
} from '../../components/action-list-modals/multi-result-modal/multi-result-modal.component';
import { SaComponent } from 'smartauction-frontend-micro-library';


@Injectable({
  providedIn: 'root'
})
export class MultiResultModalHelperService extends SaComponent{

  constructor(private readonly store: Store) {
    super();
  }

  buildDefaultLoadingTemplateData(): MultiResultContentBase {
    return {
      visibleTemplate: MultiResultModalTemplate.INITIAL_LOADING_TEMPLATE,
      happyPathData: undefined,
      errorPathData: undefined
    } as MultiResultContentBase;
  }

  buildErrorContent(headerText: string, errorMessage: string, selectedVehicleCount: number): MultiResultContentBase {
    const formattedResponse = {
      successes: [],
      failures: []
    } as UpdateResultSummary;

    return {
      visibleTemplate: MultiResultModalTemplate.ERROR_TEMPLATE,
      selectedVehicleCount: selectedVehicleCount,
      updateSummary: formattedResponse,
      updateErrorDisplays: undefined,
      headerText: headerText,
      happyPathData: undefined,
      errorPathData: {
        errorMessage: errorMessage
      } as MultiResultContentErrorPath
    } as MultiResultContentBase;
  }

  buildHappyPathTemplateContent(multiResultHappyPathData: MultiResultHappyPathData): MultiResultContentBase {
    const headerText = this.setUpdatedVehiclesHeader(multiResultHappyPathData.formattedResponse, multiResultHappyPathData.headerLabelSingular, multiResultHappyPathData.headerLabelPlural);
    const successSummary = this.createSuccessSummary(multiResultHappyPathData.formattedResponse, multiResultHappyPathData.successLabelSingular, multiResultHappyPathData.successLabelPlural);
    const failureSummary = this.createFailureSummary(multiResultHappyPathData.formattedResponse);

    return {
      visibleTemplate: MultiResultModalTemplate.RESULTS_TEMPLATE,
      selectedVehicleCount: multiResultHappyPathData.selectedVehicleCount,
      updateSummary: multiResultHappyPathData.formattedResponse,
      updateErrorDisplays: failureSummary.updateErrorDisplays,
      headerText: headerText,
      happyPathData: {
        successMessage: successSummary?.successMessage ? successSummary.successMessage : '',
        successVins: successSummary?.successVins ? successSummary.successVins : '',
        failureLabelSingular: multiResultHappyPathData.failureLabelSingular,
        failureLabelPlural: multiResultHappyPathData.failureLabelPlural
      } as MultiResultContentHappyPath,
      errorPathData: undefined
    } as MultiResultContentBase;
  }

  setUpdatedVehiclesHeader(updateResultSummary: UpdateResultSummary, updateHeaderLabelSingular: string, updateHeaderLabelPlural: string): string {
    const successNumber = updateResultSummary?.successes?.length;
    let formattedHeaderText = '';
    if(successNumber === undefined) {
      formattedHeaderText = `0 ${updateHeaderLabelPlural}`;
    } else if (successNumber && successNumber === 1) {
      formattedHeaderText = `${successNumber} ${updateHeaderLabelSingular}`;
    } else {
      formattedHeaderText = `${successNumber} ${updateHeaderLabelPlural}`;
    }
    return formattedHeaderText;
  }

  createSuccessSummary(updateResultSummary: UpdateResultSummary, successLabelSingular: string, successLabelPlural: string): SuccessSummary {
    const successSummary = {
      successVins: '',
      successMessage: ''
    } as SuccessSummary;

    if(updateResultSummary?.successes?.length > 0) {
      successSummary.successVins = this.stringifyAndformatVinsCommaSeperated(updateResultSummary.successes);

      if(this.isSingleSuccess(updateResultSummary)) {
        successSummary.successMessage = `${updateResultSummary.successes.length } ${successLabelSingular}`;
      } else {
        successSummary.successMessage = `${updateResultSummary.successes.length } ${successLabelPlural}`;
      }
    }
    return successSummary;
  }

  isSingleSuccess(updateSummary: UpdateResultSummary): boolean {
    const successes = updateSummary?.successes;
    return successes !== undefined && successes !== null && successes.length === 1;
  }

  stringifyAndformatVinsCommaSeperated(vehicleSummaries: VehicleUpdateSummary[]): string {
    const vins = [];
    if(vehicleSummaries?.length) {
      vehicleSummaries.forEach(vehicle => {
        vins.push(vehicle.vin);
      });
      return vins.join(', ');
    } else {
      return '';
    }
  }

  createFailureSummary(updateResultSummary: UpdateResultSummary): FailureSummary {
    const failureSummary = {
      failureVins: '',
      updateErrorDisplays: []
    } as FailureSummary;

    if(updateResultSummary?.failures?.length > 0) {
      const uniqueErrorDisplays = this.buildUniqueUpdateErrorDisplays(updateResultSummary.failures);
      failureSummary.updateErrorDisplays = this.addVinsToUpdateErrorDisplayByCode(uniqueErrorDisplays, updateResultSummary.failures);
    }
    return failureSummary;
  }

  buildUniqueUpdateErrorDisplays(failures: VehicleUpdateSummary[]): UpdateErrorDisplay[] {
    const uniqueErrorCodes = [];
    if(failures?.length) {
      failures.forEach(failure => {
        if(!uniqueErrorCodes.includes(failure.errors[0].errorCode)) {
          uniqueErrorCodes.push(failure.errors[0].errorCode);
        }
      });
      return this.buildPartialUpdateErrorDisplay(uniqueErrorCodes, failures);
    } else {
      console.log('No failures to build error displays');
    }
  }

  buildPartialUpdateErrorDisplay(uniqueErrorCodes: string[], failures: VehicleUpdateSummary[]): UpdateErrorDisplay[] {
    const partialUpdateErrorDisplays = [];
    if(uniqueErrorCodes?.length > 0) {
      uniqueErrorCodes.forEach(errorCode => {
        const failure = failures.find(failure => failure.errors[0].errorCode === errorCode);
        const failureMessage = (failure?.errors[0]?.errorDescription?.length > 0) ? failure.errors[0].errorDescription :
          failure?.errors[0].errorMessage ? failure?.errors[0].errorMessage :
            'Unknown Error : Error Not Mapped';
        const sanitizedErrorMessage = failureMessage.toLowerCase().replace(/\./g, '');
        const updateErrorDisplay = {
          errorCode: errorCode,
          errorMessage: sanitizedErrorMessage,
          vins: [],
          stringifiedVins: ''
        } as UpdateErrorDisplay;
        partialUpdateErrorDisplays.push(updateErrorDisplay);
      });
    }
    return partialUpdateErrorDisplays;
  }

  addVinsToUpdateErrorDisplayByCode(uniqueUpdateErrorDisplays: UpdateErrorDisplay[], failures: VehicleUpdateSummary[]) : UpdateErrorDisplay[] {
    const uniqueErrorDisplays = uniqueUpdateErrorDisplays;
    uniqueErrorDisplays.forEach(display => {
      failures.forEach(failure => {
        if(display.errorCode === failure.errors[0].errorCode) {
          display.vins.push(failure.vin);
        }
      });
    });
    uniqueErrorDisplays.forEach(display => {
      display.stringifiedVins = display.vins.join(', ');
    });
    return uniqueErrorDisplays;
  }

  formatMultiResponseModalResponseDto(multiResponseModalResults: MultiResultModalItemResult[]): UpdateResultSummary {
    const successes = [];
    const failures = [];

    if(multiResponseModalResults?.length > 0) {
      multiResponseModalResults.forEach(result => {
        const vehicleSummary = {
          vin: result.vin,
          errors: result.success ? [] : result.errors
        } as VehicleUpdateSummary;

        result.success ? successes.push(vehicleSummary) : failures.push(vehicleSummary);
      });
    }

    return {
      successes: successes,
      failures: failures
    } as UpdateResultSummary;
  }

  selectAllFailedVehicles(failedAuctionItems : AuctionItemSell[] ): void {
    if(failedAuctionItems && failedAuctionItems?.length > 0) {
      this.store.dispatch(new ClearAllSelectedVehicles());
      failedAuctionItems.forEach(failedAuctionItem => {
        this.store.dispatch(new AddVehicleToSelections(failedAuctionItem));
      });
    } else {
      console.log('No update failures to select');
      this.store.dispatch(new ClearAllSelectedVehicles());
    }
  }
}

export interface MultiResultHappyPathData {
  formattedResponse: UpdateResultSummary;
  selectedVehicleCount: number;
  headerLabelSingular: string;
  headerLabelPlural: string;
  successLabelSingular: string;
  successLabelPlural: string;
  failureLabelSingular: string;
  failureLabelPlural: string;
}
